import React, { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import IconButton from '../IconButton';
import Icon from '../Icon';
import Input from '../Input';
import Tags from '../Tags';
import FormLabel from '../FormLabel';
import InputDataList from '../InputDataList';
import { StyledInputTags } from './styled';
import useTheme from '../../hooks/useTheme';
import { makeStyles } from '../../utils/styles';
import { findNewTag } from '../../utils/helpers';
import Button from '../Button';
import Box from '../Box';
import ScrollArea from '../ScrollArea';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles(css => ({
    iconButton: css`
        padding: 14px;
    `,
    tags: css`
    `,
    box: css`
        display: flex;
        margin-top: 10px;
    `
}));

const InputTags = forwardRef(({ id, placeholder, label, name, value, error, disabled, errorText, color, readonly, tags, tagsHints, enableEnterKeyDown, enableDeleteAll, useHintsToFindTag, isReturnNewTag, onChange, onClickAdd, onEnterKeyDown, onClickDelete, onClickDeleteAll, customStyle, scrollArea }, ref) => {
    const { t } = useTranslation()
    const innerRef = useRef(null);
    const theme = useTheme();
    const styles = useStyles();

    const escFunction = useCallback((event) => {
        if (enableEnterKeyDown && event.keyCode === 13) {
            if (!!onEnterKeyDown) {
                event.preventDefault()
                const tagName = innerRef.current.value;
                if (useHintsToFindTag) {
                    onEnterKeyDown(findNewTag(tagsHints, tags, tagName, isReturnNewTag));
                } else {
                    onEnterKeyDown(tagName);
                }
                innerRef.current.value = "";
            }
        }
    }, [enableEnterKeyDown, onEnterKeyDown, useHintsToFindTag, isReturnNewTag, tags, tagsHints]);

    const heightModifiers = useMemo(() => {
        if(scrollArea?.heightModifiers) {
            return {
                ...scrollArea.heightModifiers,
                offset: scrollArea.heightModifiers?.offset ? scrollArea.heightModifiers?.offset + 84 : 0,
            }
        }
        return {
            isCalc: false,
            offset: 84
        }
    }, [scrollArea]);

    useEffect(() => {
        document.addEventListener("keydown", escFunction, false);

        return () => {
            document.removeEventListener("keydown", escFunction, false);
        };
    }, [escFunction]);

    const handleClickAdd = () => {
        if (enableEnterKeyDown && useHintsToFindTag) {
            const tagName = innerRef.current.value;
            if (useHintsToFindTag) {
                onClickAdd(findNewTag(tagsHints, tags, tagName));
            } else {
                onClickAdd(tagName);
            }
            innerRef.current.value = "";
        } else {
            onClickAdd();
        }
    }

    return (
        <StyledInputTags customStyle={customStyle}>
            {label && <FormLabel htmlFor={id}>{label}</FormLabel>}
            {
                scrollArea ?
                    <ScrollArea
                        heightModifiers={heightModifiers}
                    >
                        <Tags
                            tags={tags}
                            onClickDelete={onClickDelete}
                            customStyle={styles.tags}
                            readonly={readonly}
                        />
                    </ScrollArea>
                    :
                    <Tags
                        tags={tags}
                        onClickDelete={onClickDelete}
                        customStyle={styles.tags}
                        readonly={readonly}
                    />
            }
            <Box
                customStyle={styles.box}
            >
                <Input
                    ref={enableEnterKeyDown ? innerRef : ref}
                    id={id}
                    placeholder={placeholder}
                    type="text"
                    name={name}
                    value={value}
                    error={error}
                    fullWidth={true}
                    maxWidth="430px"
                    autoComplete="off"
                    disabled={disabled}
                    errorText={errorText}
                    startAdornment={
                        <IconButton
                            customStyle={styles.iconButton}
                            onClick={handleClickAdd}
                        >
                            <Icon name="add" height={12} width={12} fill={theme.colors.darkGrey} />
                        </IconButton>
                    }
                    color={color}
                    list={`list-${id}`}
                    onChange={onChange}
                />
                {
                    tagsHints &&
                    <InputDataList
                        id={`list-${id}`}
                        datalist={tagsHints}
                    />
                }
                {
                    enableDeleteAll &&
                    <Button onClick={onClickDeleteAll} variant="text" startIcon={<Icon name="delete" width={12} height={12} />}>
                        {t("Usuń wszystkie")}
                    </Button>
                }
            </Box>
        </StyledInputTags>
    );
});

InputTags.propTypes = {
    id: PropTypes.string,
    placeholder: PropTypes.string,
    label: PropTypes.string,
    name: PropTypes.string,
    value: PropTypes.string,
    error: PropTypes.bool,
    disabled: PropTypes.bool,
    errorText: PropTypes.string,
    color: PropTypes.string,
    readonly: PropTypes.bool,
    tags: PropTypes.array,
    tagsHints: PropTypes.array,
    enableEnterKeyDown: PropTypes.bool,
    enableDeleteAll: PropTypes.bool,
    useHintsToFindTag: PropTypes.bool,
    onChange: PropTypes.func,
    onEnterKeyDown: PropTypes.func,
    onClickAdd: PropTypes.func,
    onClickDelete: PropTypes.func,
    onClickDeleteAll: PropTypes.func,
    customStyle: PropTypes.array,
    scrollArea: PropTypes.object
};

InputTags.defaultProps = {
    type: 'text',
    error: false,
    fullWidth: false,
    maxWidth: 'none',
    autoComplete: 'on',
    disabled: false,
    readonly: false,
    tags: [],
    tagsHints: [],
    onChange: undefined,
    onClickAdd: undefined,
    onClickDelete: undefined,
    onClickDeleteAll: undefined,
    customStyle: [],
};

export default React.memo(InputTags);
